/*
 * PG Free Gift — shared cart controller
 * -------------------------------------
 * Self-contained (no theme events required). Exposes `window.PGFreeGift` with a
 * single `reconcile()` entry point used by:
 *   • snippets/pg-cart-toast.liquid  (after add to cart / quick add)
 *   • sections/pg-cart.liquid        (after quantity / remove changes)
 *   • this file's own load-time pass  (returning carts)
 *
 * Rule: exactly ONE free gift while the cart has any other product — one per
 * order, not one per item — removed automatically once no other products remain.
 *
 * Config comes from window.PGFreeGiftConfig (rendered by snippets/free-gift.liquid
 * only when the theme setting is on), so nothing runs on stores that opt out.
 */
(function () {
  var CFG = window.PGFreeGiftConfig || { enabled: false };
  var GIFT_PROP = '_free_gift';

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }
  function url(path) {
    return root().replace(/\/$/, '/') + path;
  }

  var resolvedVariantId = CFG.variantId || null;
  var variantLookup = null;

  function isEnabled() {
    return CFG.enabled === true && !!CFG.handle;
  }

  function isGift(item) {
    if (!item) return false;
    if (CFG.handle && item.handle === CFG.handle) return true;
    return !!(item.properties && item.properties[GIFT_PROP]);
  }

  function postJSON(path, payload) {
    return fetch(url(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    }).then(function (r) {
      return r.json();
    });
  }

  function getCart() {
    return fetch(url('cart.js'), { headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  function getVariantId() {
    if (resolvedVariantId) return Promise.resolve(resolvedVariantId);
    if (!CFG.handle) return Promise.resolve(null);
    if (!variantLookup) {
      variantLookup = fetch(url('products/' + CFG.handle + '.js'), { headers: { Accept: 'application/json' } })
        .then(function (r) {
          return r.ok ? r.json() : null;
        })
        .then(function (product) {
          if (!product || !product.variants || !product.variants.length) return null;
          var variant = null;
          for (var i = 0; i < product.variants.length; i++) {
            if (product.variants[i].available) {
              variant = product.variants[i];
              break;
            }
          }
          if (!variant) variant = product.variants[0];
          resolvedVariantId = variant.id;
          return resolvedVariantId;
        })
        .catch(function () {
          return null;
        });
    }
    return variantLookup;
  }

  function addGift(sections) {
    return getVariantId().then(function (id) {
      if (!id) return null;
      var item = { id: id, quantity: 1, properties: {} };
      item.properties[GIFT_PROP] = 'true';
      var payload = { items: [item] };
      if (sections) payload.sections = sections;
      return postJSON('cart/add.js', payload);
    });
  }

  function changeLine(key, quantity, sections) {
    var payload = { id: key, quantity: quantity };
    if (sections) {
      payload.sections = sections;
      payload.sections_url = window.location.pathname;
    }
    return postJSON('cart/change.js', payload);
  }

  /**
   * Reconcile the free gift against the current cart.
   * @param {{ sections?: string }} [options] request rendered section HTML on mutation
   * @returns {Promise<{changed:boolean, giftAdded:boolean, giftRemoved:boolean, cart:object, giftItem:object|null, sections:object|null}>}
   */
  function reconcile(options) {
    options = options || {};
    var sections = options.sections || null;
    var result = { changed: false, giftAdded: false, giftRemoved: false, cart: null, giftItem: null, sections: null };

    if (!isEnabled()) {
      return getCart().then(function (cart) {
        result.cart = cart;
        return result;
      });
    }

    return getCart()
      .then(function (cart) {
        var gifts = cart.items.filter(isGift);
        var others = cart.items.filter(function (i) {
          return !isGift(i);
        });
        var chain = Promise.resolve();
        var lastResp = null;

        if (others.length === 0) {
          // No other products — remove any gift so the cart empties out.
          gifts.forEach(function (g) {
            chain = chain.then(function () {
              return changeLine(g.key, 0, sections).then(function (r) {
                lastResp = r;
                result.changed = true;
                result.giftRemoved = true;
              });
            });
          });
        } else if (gifts.length === 0) {
          // Products present, no gift yet — add exactly one.
          chain = chain.then(function () {
            return addGift(sections).then(function (r) {
              if (r && !r.status) {
                lastResp = r;
                result.changed = true;
                result.giftAdded = true;
              }
            });
          });
        } else {
          // Gift present — normalise to a single line at quantity 1.
          var totalQty = gifts.reduce(function (s, g) {
            return s + g.quantity;
          }, 0);
          if (gifts.length > 1 || totalQty !== 1) {
            for (var i = 1; i < gifts.length; i++) {
              (function (g) {
                chain = chain.then(function () {
                  return changeLine(g.key, 0, sections).then(function (r) {
                    lastResp = r;
                  });
                });
              })(gifts[i]);
            }
            if (gifts[0].quantity !== 1) {
              chain = chain.then(function () {
                return changeLine(gifts[0].key, 1, sections).then(function (r) {
                  lastResp = r;
                });
              });
            }
            result.changed = true;
          }
        }

        return chain
          .then(function () {
            if (sections && lastResp && lastResp.sections) result.sections = lastResp.sections;
            return getCart();
          })
          .then(function (freshCart) {
            result.cart = freshCart;
            result.giftItem = freshCart.items.filter(isGift)[0] || null;
            return result;
          });
      })
      .catch(function (error) {
        console.warn('[pg-free-gift] reconcile failed', error);
        return result;
      });
  }

  // Serialise reconciles so rapid interactions can't race each other.
  var queue = Promise.resolve();
  function safeReconcile(options) {
    var run = queue.then(function () {
      return reconcile(options);
    });
    queue = run.catch(function () {});
    return run;
  }

  window.PGFreeGift = {
    config: CFG,
    isEnabled: isEnabled,
    isGift: isGift,
    getCart: getCart,
    reconcile: safeReconcile,
  };

  // Load-time pass: fix a returning cart and reflect it in the header / cart page.
  function initialReconcile() {
    if (!isEnabled()) return;
    var page = window.PGCartPage || null;
    var sections = page && page.sectionId ? page.sectionId : null;

    safeReconcile({ sections: sections }).then(function (res) {
      if (!res.changed) return;
      if (window.PGCartToast && typeof window.PGCartToast.setCount === 'function' && res.cart) {
        window.PGCartToast.setCount(res.cart.item_count);
      }
      if (page) {
        if (res.sections && res.sections[page.sectionId]) {
          page.render(res.sections[page.sectionId]);
        } else {
          page.refresh();
        }
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialReconcile);
  } else {
    initialReconcile();
  }
})();
